import { isApolloError, type ApolloError } from '@apollo/client'
import * as Sentry from '@sentry/react'
import { Button, Space, Typography } from 'antd'
import axios, { type AxiosError } from 'axios'
import {
  notificationHandler,
  simpleErrorNotification,
} from './notificationHandler'
import { ParagraphProps } from 'antd/lib/typography/Paragraph'
const { Paragraph } = Typography

/**
 * Wraps the antd notification helper to provide a consistent interface for
 * handling client side errors
 *
 * @param error An error object or error message. The handler will check for an
 * Apollo Error, normal Error, and can even handle neither.
 * @param extraMessages any additional messages you want included with the notification
 * @param callback a callback function to run after the message is finished
 * @example
 * const messageDone = () => console.log('Done!')
 *
 * const { data, loading } = useSomeGraphQLQuery({
 *  variables: { someVariable: value },
 *  onError: (err) => errorHandler(
 *    err,
 *    ['You need additional permissions', 'Some more things you need to know'],
 *    messageDone
 *  ),
 * })
 *
 * // dead simple use
 * const { data } = useSomeOtherGraphQLQuery({
 *  variables: { anotherVariable: anotherValue },
 *  onError: errorHandler
 * })
 */
export function errorHandler(
  error: unknown,
  extraMessages?: string[],
  callback?: () => void
) {
  console.error(error)
  logError(error, extraMessages)
  const response = isPropertyBag(error) ? error.response : undefined
  const data = isPropertyBag(response) ? response.data : undefined
  const startsWith = isPropertyBag(data) ? data.startsWith : undefined
  if (
    (typeof data === 'string' && data.startsWith('Proxy error')) ||
    (typeof startsWith === 'function' &&
      Boolean(
        (startsWith as (this: unknown, searchString: string) => unknown).call(
          data,
          'Proxy error'
        )
      ))
  )
    return proxyError()
  else if (isApolloErrorValue(error)) {
    const { message, networkError, graphQLErrors } = error
    if (message.includes('Proxy')) return proxyError()
    else if (
      message === `Unexpected token '<', "<!DOCTYPE "... is not valid JSON` &&
      isPropertyBag(networkError) &&
      'bodyText' in networkError
    ) {
      return htmlError(networkError.bodyText as string | undefined)
    }
    const messages: Message[] =
      extraMessages?.map((message) => ({ message })) ?? []
    const result = isPropertyBag(networkError) ? networkError.result : undefined
    const networkErrors =
      isPropertyBag(result) && Array.isArray(result.errors) ? result.errors : []
    messages.push(
      ...networkErrors.map<Message>((error) => {
        const { message } = error as { message: string }
        return {
          message,
          copyable: true,
        }
      })
    )
    messages.push(
      ...graphQLErrors.reduce<Message[]>((messages, { extensions }) => {
        if (!extensions) return messages
        delete extensions.code
        delete extensions.exception
        if (Object.keys(extensions).length)
          messages.push({
            message: JSON.stringify(extensions, null, 2),
            copyable: true,
          })
        return messages
      }, [])
    )
    return additionalDetails({ message, callback, messages })
  } else if (axios.isAxiosError(error)) {
    const { response, message: __message } = error as AxiosError<unknown>
    const { config, data } = response ?? {}
    const { url } = config ?? {}
    const responseData = isPropertyBag(data) ? data : {}
    const _message = responseData.message as string | undefined
    const extras = responseData.extras
    const message = _message ?? __message ?? 'Unknown Error'
    const messages: Message[] =
      extraMessages?.map((message) => ({ message })) ?? []
    messages.push({
      message: JSON.stringify(
        { url, ...((extras ?? {}) as Record<string, unknown>) },
        null,
        2
      ),
      copyable: true,
    })
    return additionalDetails({
      message,
      callback,
      messages,
    })
  } else if (String(error).includes('DOCTYPE')) return htmlError(String(error))
  return additionalDetails({
    message: String(error),
    callback,
    messages: extraMessages?.map((message) => ({ message })),
  })
}

function logError(error: unknown, extraMessages?: string[]) {
  try {
    Sentry.logger.error('Client-side error handled', {
      ...getErrorAttributes(error),
      extraMessages,
    })
  } catch {
    // Sentry logging should never interrupt the user-facing error flow.
    console.error('Sentry failed to log an error')
  }
}

function getErrorAttributes(error: unknown): Record<string, unknown> {
  if (isApolloErrorValue(error))
    return {
      type: 'apollo',
      message: error.message,
      graphQLErrors: error.graphQLErrors.map(({ extensions, message }) => ({
        extensions,
        message,
      })),
      networkError: error.networkError?.message,
    }

  if (axios.isAxiosError(error)) {
    const { config, response } = error as AxiosError<unknown>
    const data = response?.data
    const responseData = isPropertyBag(data) ? data : undefined
    return {
      type: 'axios',
      message: error.message,
      status: response?.status,
      statusText: response?.statusText,
      url: config?.url,
      responseMessage: responseData?.message,
      responseExtras: responseData?.extras,
    }
  }

  if (error instanceof Error)
    return {
      type: 'error',
      message: error.message,
      name: error.name,
      stack: error.stack,
    }

  return {
    type: typeof error,
    message: String(error),
  }
}

function isPropertyBag(value: unknown): value is Record<string, unknown> {
  return (
    (typeof value === 'object' && value !== null) || typeof value === 'function'
  )
}

function isApolloErrorValue(error: unknown): error is ApolloError {
  if (!isPropertyBag(error)) return false
  // Apollo's runtime check is duck-typed, but its declaration requires Error.
  return isApolloError(error as unknown as Error)
}

function htmlError(htmlString?: string) {
  console.log({ htmlString })
  if (!htmlString)
    return simpleErrorNotification(
      'There was an error returned in HTML format that could not be parsed'
    )
  return notificationHandler({
    title: 'Error returned as HTML',
    alternateType: 'error',
    details: (
      <div style={{ overflow: 'auto', maxHeight: 300 }}>
        <div dangerouslySetInnerHTML={{ __html: htmlString }} />
      </div>
    ),
  })
}

const proxyError = () =>
  simpleErrorNotification(
    'There was an issue connecting to the server.',
    'It may be temporarily offline. Try again in a few minutes...'
  )

interface Message extends Pick<ParagraphProps, 'ellipsis' | 'copyable'> {
  message: string
}

const getDetails = (messages?: Message[]) =>
  !messages?.length ? null : (
    <Typography.Paragraph>
      {messages.map(({ message, copyable, ellipsis }) => (
        <Typography.Paragraph copyable={copyable} ellipsis={ellipsis}>
          {message}
        </Typography.Paragraph>
      ))}
    </Typography.Paragraph>
  )

interface AdditionalDetailsProps {
  message: string
  messages?: Message[]
  callback?: () => void
}

function additionalDetails({
  message,
  messages,
  callback,
}: AdditionalDetailsProps) {
  const key = 'additional-errors'
  const updateContent = () =>
    notificationHandler({
      key,
      title: 'Additional Error Details',
      alternateType: 'error',
      details: getDetails(messages),
      duration: 'infinite',
    })

  notificationHandler({
    key,
    title: message,
    alternateType: 'error',
    details: !!messages?.length ? (
      <Space direction="vertical">
        <Paragraph>There are additional details</Paragraph>
        <Button
          type="primary"
          style={{ float: 'right' }}
          onClick={() => updateContent()}
        >
          Show Details
        </Button>
      </Space>
    ) : undefined,
    onClose: callback,
  })
}
